import React, { useState } from "react";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { Loader, MoreHorizontal, ThumbsDown, ThumbsUp } from "lucide-react";
import YouTubeFooter from "./YouTubeFooter";

const getChannelInitials = (name?: string) => {
  if (!name) return "YT";
  const parts = name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
  return parts.slice(0, 2) || "YT";
};

const YouTubeVideoPreview = ({ post }: { post: any }) => {
  const isMobile = useIsMobile(900);
  const [imgError, setImgError] = useState(false);

  const mainMedia = post.media && post.media.length > 0 ? post.media[0] : null;

  return (
    <>
      <div className="max-w-5xl  mx-auto bg-white rounded-lg overflow-hidden">
        {/* Video Thumbnail */}
        {mainMedia && (
          <div className="w-full aspect-[16/9] bg-black rounded-xl overflow-hidden relative">
            {mainMedia.isProcessing ? (
              <>
                {mainMedia.thumbnail && (
                  <img
                    src={mainMedia.thumbnail}
                    alt="Media"
                    className="w-full h-full object-contain"
                  />
                )}
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                  <span className="text-white text-sm font-medium">Video Processing</span>
                  <Loader className="ml-2 animate-spin text-secondary" />
                </div>
              </>
            ) : (
              <video
                controls
                preload="metadata"
                src={mainMedia.signedUrl || mainMedia.url}
                className="w-full h-full object-contain"
              />
            )}
          </div>
        )}

        <div className="pt-3 space-y-3 mb-2">
          {/* Row 1 : Title */}
          <h2 className="text-lg font-semibold">{post.title}</h2>

          {/* Row 2 */}
          <div className={`flex ${isMobile ? "flex-col gap-3" : "items-center justify-between"}`}>
            {/* LEFT SIDE */}
            <div className={`flex items-center gap-3 ${isMobile ? "justify-between" : ""}`}>
              <div className="flex items-center gap-3">
                {/* Avatar */}
                {post?.userImage && !imgError ? (
                  <img
                    src={post.userImage}
                    alt="channel avatar"
                    className="w-9 h-9 rounded-full object-cover"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center font-semibold text-sm">
                    {getChannelInitials(post?.displayName)}
                  </div>
                )}

                {/* Channel Name */}
                <div className="font-medium">
                  {post.displayName || "YouTube Channel"}
                </div>
              </div>

              {/* Subscribe — always visible, pushed right on mobile */}
              <button className={`bg-black text-white rounded-full hover:bg-gray-900 ${isMobile ? "px-2 py-2 text-xs" : "px-4 py-2 text-sm"}`}>
                Subscribe
              </button>
            </div>

            {/* RIGHT SIDE */}
            <div className={`flex items-center gap-2 ${isMobile ? "w-full" : ""}`}>
              {/* LIKE + DISLIKE COMBINED */}
              <div className="flex items-center bg-[#f2f2f2] rounded-full">
                <button className="flex items-center gap-2 px-4 py-2 hover:bg-gray-200 rounded-l-full">
                  <ThumbsUp size={18} />
                  <span>1.1K</span>
                </button>

                <div className="w-px h-5 bg-gray-300" />

                <button className="px-4 py-2 hover:bg-gray-200 rounded-r-full">
                  <ThumbsDown size={18} />
                </button>
              </div>

              {/* Share + More — pushed to far right on mobile */}
              <div className={`flex items-center gap-2 ${isMobile ? "ml-auto" : ""}`}>
                {/* Share */}
                <button className="flex items-center gap-2 px-4 py-2 bg-[#f2f2f2] rounded-full hover:bg-gray-200">
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="24"
                      viewBox="0 0 24 24"
                      width="24"
                      focusable="false"
                      aria-hidden="true"
                      style={{
                        pointerEvents: "none",
                        display: "block",
                        width: "24px",
                        height: "24px",
                        flexShrink: 0,
                      }}
                    >
                      <path d="M10 3.158V7.51c-5.428.223-8.27 3.75-8.875 11.199-.04.487-.07.975-.09 1.464l-.014.395c-.014.473.578.684.88.32.302-.368.61-.73.925-1.086l.244-.273c1.79-1.967 3-2.677 4.93-2.917a18.011 18.011 0 012-.112v4.346a1 1 0 001.646.763l9.805-8.297 1.55-1.31-1.55-1.31-9.805-8.297A1 1 0 0010 3.158Zm2 6.27v.002-4.116l7.904 6.688L12 18.689v-4.212l-2.023.024c-1.935.022-3.587.17-5.197 1.024a9 9 0 00-1.348.893c.355-1.947.916-3.39 1.63-4.425 1.062-1.541 2.607-2.385 5.02-2.485L12 9.428Z" />
                    </svg>
                  </div>
                  {!isMobile && <span>Share</span>}
                </button>

                {/* More */}
                <button className="flex items-center justify-center w-10 h-10 bg-[#f2f2f2] rounded-full hover:bg-gray-200">
                  <MoreHorizontal size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <YouTubeFooter post={post} />
    </>
  );
};

export default YouTubeVideoPreview;
